import { saveAs } from "file-saver";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import * as XLSX from "xlsx";
import Layout from "../Layout/Layout.jsx";
import MemberApprove from "../Modals/MemberApprove.jsx";
import "../Styles/component.css";
import "../Styles/global.css";
import "../Styles/index.css";
import {
  requestDepartmentList,
  requestMemberCounts,
  requestMemberExcelList,
  requestMemberList,
  requestRankingList,
} from "./Members.api.js";
import { buildMemberFilterParams } from "./Members.filterParams.js";
import MembersInfoBar from "./Members.infoBar.jsx";
import MemberManage from "./Members.manage.jsx";
import MemberRankingSection from "./Members.ranking.jsx";
import MembersTabSwitch from "./Members.tabSwitch.jsx";
import {
  buildMemberExcelColumns,
  getMemberExcelCellValue,
} from "./Members.tableConfig.js";
import { getCompanyProfile } from "../Utils/sessionUtils.js";
import DepartmentManage from "../Modals/DepartmentManage.jsx";
import LayoutModal from "../Layout/LayoutModal.jsx";
import { isCoupangCompany } from "../Utils/companyUtils.js";

const MembersContent = () => {
  const companyProfile = getCompanyProfile();
  const { companyCode } = companyProfile;
  const showSecretCode = companyCode === "citibank";
  const maskMemberEmail = isCoupangCompany(companyProfile);
  const canManageDepartmentAndRelation =
    companyCode === "citibank" ||
    companyCode === "test" ||
    companyCode === "runpoint";
  const [manageSelected, setManageSelected] = useState(true);
  const [rankingSelected, setRankingSelected] = useState(false);

  const [total_member, setTotal_member] = useState(0);
  const [memberData_list, setMemberData_list] = useState([]);
  const [ranking_list, setRanking_list] = useState([]);

  const [page_forMember, setPage_forMember] = useState(1);
  const [page_forRanking, setPage_forRanking] = useState(1);
  const offset = 10;

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [searchWord, setSearchWord] = useState("");
  const [department_forMember, setDepartment_forMember] = useState("");
  const [sort_forMember, setSort_forMember] = useState("");
  const [nation_forMember, setNation_forMember] = useState("");
  const [nation_forRanking, setNation_forRanking] = useState("");
  const [status, setStatus] = useState("");
  const [clickMemberApprove, setClickMemberApprove] = useState(false);

  const [capacity_count, setCapacity_count] = useState(0);
  const [possible_count, setPossible_count] = useState(0);
  const [waiting_count, setWaiting_count] = useState(0);
  const [period, setPeriod] = useState("day");

  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const hasLoadedMemberSummary = useRef(false);
  const lastMemberRequestKey = useRef(null);
  const lastRankingRequestKey = useRef(null);

  const navigate = useNavigate();

  // 추가 2026-02-03
  useEffect(() => {
    if (startTime === null && endTime === null) {
      const today = new Date();
      setStartTime(today);
      setEndTime(today);
    }
  }, []);   // 최초 1회만 실행

  const handleManageClick = () => {
    setRankingSelected(false);
    setManageSelected(true);
  };
  const handleRankingClick = () => {
    setManageSelected(false);
    setRankingSelected(true);
  };

  const getCountInfo = async () => {
    try {
      const data = await requestMemberCounts({ navigate });

      if (data.code === 200) {
        setCapacity_count(data.data.count);
        setPossible_count(data.data.approvalPossibleCount);
        setWaiting_count(data.data.approvalWaitingCount);
      }
    } catch (error) {
      console.error("구성원 카운트 정보 가져오기 중 에러:", error);
    }
  };

  // 부서 리스트
  const getDepartmentList = async () => {
    try {
      const res = await requestDepartmentList({ navigate });
      if (res.code === 200) {
        setDepartmentList(res.data.rows);
      } else {
        console.error("부서 리스트 불러오기 실패:", res);
      }
    } catch (error) {
      console.error("부서 리스트 가져오기 중 에러", error);
    }
  };

  const getMemberList = async () => {
    try {
      const data = await requestMemberList({
        page: page_forMember,
        offset,
        filterParams: buildMemberFilterParams({
          searchWord,
          startTime,
          endTime,
          sortForMember: sort_forMember,
          nationForMember: nation_forMember,
          status,
          departmentForMember: department_forMember,
          departmentList,
          emptyCCode: "",
          includeDepartmentFilter: true,
        }),
        navigate,
      });

      if (data.code === 200) {
        setTotal_member(data.data.count);
        setMemberData_list(data.data.rows);
      }
    } catch (error) {
      console.log("구성원리스트 가져오기 중 에러:", error);
      throw error;
    }
  };

  const refreshMemberList = async () => {
    lastMemberRequestKey.current = null;
    await getMemberList();
  };

  const getRankingList = async () => {
    try {
      const data = await requestRankingList({
        page: page_forRanking,
        period,
        nationForRanking: nation_forRanking,
        navigate,
      });
      if (data.code === 200) {
        setRanking_list(data.data.rows);
      }
    } catch (error) {
      console.log("랭킹리스트 가져오기 중 에러:", error);
      throw error;
    }
  };

  const handleDownloadExcel = async () => {
    try {
      if (total_member === 0) {
        return;
      }

      const body = buildMemberFilterParams({
        searchWord,
        startTime,
        endTime,
        sortForMember: sort_forMember,
        nationForMember: nation_forMember,
        status,
        departmentForMember: department_forMember,
        departmentList,
        includeDepartmentFilter: true,
      });
      const res = await requestMemberExcelList({
        filterParams: body,
        navigate,
      });

      if (res.code === 200) {
        const memberExcelColumns = buildMemberExcelColumns({
          canManageDepartmentAndRelation,
          showSecretCode,
        });
        const excelRows = res.data.rows.map((row, index) =>
          Object.fromEntries(
            memberExcelColumns.map((column) => [
              column.label,
              getMemberExcelCellValue({
                row,
                index,
                columnKey: column.key,
                maskEmail: maskMemberEmail,
              }),
            ])
          )
        );

        const ws = XLSX.utils.json_to_sheet(excelRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "구성원 명단");

        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], { type: "application/octet-stream" });

        saveAs(blob, "랭킹마라톤 대시보드_구성원 명단.xlsx");
      }
    } catch (error) {
      console.log("엑셀 다운로드 중 에러");
      throw error;
    }
  };

  // 부서 리스트와 용량 정보는 최초 1회만
  useEffect(() => {
    if (hasLoadedMemberSummary.current) {
      return;
    }

    hasLoadedMemberSummary.current = true;
    getCountInfo();
    getDepartmentList();
  }, []);

  const memberListParams = useMemo(
    () => ({
      department: department_forMember,
      sort: sort_forMember,
      nation: nation_forMember,
      page: page_forMember,
      status,
    }),
    [
      department_forMember,
      sort_forMember,
      nation_forMember,
      page_forMember,
      status,
    ]
  );

  const RankListParams = useMemo(
    () => ({
      period: period,
      nation: nation_forRanking,
      page: page_forRanking,
    }),
    [period, nation_forRanking, page_forRanking]
  );

  useEffect(() => {
    const requestKey = JSON.stringify(memberListParams);

    if (lastMemberRequestKey.current === requestKey) {
      return;
    }

    lastMemberRequestKey.current = requestKey;
    getMemberList();
  }, [memberListParams]);

  useEffect(() => {
    const requestKey = JSON.stringify(RankListParams);

    if (lastRankingRequestKey.current === requestKey) {
      return;
    }

    lastRankingRequestKey.current = requestKey;
    getRankingList();
  }, [RankListParams]);

  const handleOpenDepartmentModal = () => {
    setIsDepartmentModalOpen(true);
  };
  const handleCloseDepartmentModal = () => {
    setIsDepartmentModalOpen(false);
  };

  return (
    <>
      {isDepartmentModalOpen && (
        <LayoutModal
          Modal={
            <DepartmentManage
              handleCloseDepartmentModal={handleCloseDepartmentModal}
            />
          }
        />
      )}

      <div>
        {/* 구성원 용량 | 승인 가능 구성원 | 승인 대기 구성원 */}
        {clickMemberApprove && (
          <MemberApprove handleClickX={() => setClickMemberApprove(false)} />
        )}
        <MembersInfoBar
          capacityCount={capacity_count}
          possibleCount={possible_count}
          waitingCount={waiting_count}
          onOpenMemberApprove={() => setClickMemberApprove(true)}
        />

        <MemberBox>
          <MembersTabSwitch
            manageSelected={manageSelected}
            rankingSelected={rankingSelected}
            onManageClick={handleManageClick}
            onRankingClick={handleRankingClick}
          />
          {manageSelected && (
            <MemberManage
              memberData_list={memberData_list}
              total_member={total_member}
              searchWord={searchWord}
              setSearchWord={(value) => setSearchWord(value)}
              sort={sort_forMember}
              departmentList={departmentList}
              setDepartment={(value) => {
                setDepartment_forMember(value);
              }}
              setSort={(value) => setSort_forMember(value)}
              nation={nation_forMember}
              setNation={(value) => setNation_forMember(value)}
              getMemberList={getMemberList}
              refreshMemberList={refreshMemberList}
              maskMemberEmail={maskMemberEmail}
              page={page_forMember}
              setPage={setPage_forMember}
              startTime={startTime}
              endTime={endTime}
              setStartTime={setStartTime}
              setEndTime={setEndTime}
              handleDownloadExcel={handleDownloadExcel}
              status={status}
              setStatus={(value) => setStatus(value)}
              handleOpenDepartmentModal={handleOpenDepartmentModal}
            />
          )}

          {rankingSelected && (
            <MemberRankingSection
              rankingList={ranking_list}
              periodOptionChange={(value) => setPeriod(value)}
              nationOptionChange={(value) => setNation_forRanking(value)}
              page={page_forRanking}
              setPage={setPage_forRanking}
              totalMember={total_member}
            />
          )}
        </MemberBox>
      </div>
    </>
  );
};

const MemberBox = styled.div`
  width: 1500px;
  height: auto;
  border-radius: 20px;
  margin: 30px 0 40px 30px;
  background-color: #ffffff;
  box-shadow: 0 0 20px 0 #f2f2f2;
  padding: 10px 30px 30px 30px;
  .activeButton {
    width: 113px;
    height: 56px;
  }
`;

const Members = () => {
  return <Layout page={<MembersContent />} />;
};
export default Members;
